import React from "react";
import ThemePageShell, { ThemeField, isValidHex } from "./ThemePageShell";
import { ThemePayload } from "../../context/ThemeContext";

const fields: ThemeField[] = [
  {
    name: "themeName",
    label: "Theme name",
    type: "text",
    placeholder: "Modern Minimal",
    required: true,
    helper: "This internal name identifies the theme version.",
  },
  {
    name: "accentPalette",
    label: "Accent palette",
    type: "select",
    placeholder: "Choose palette",
    required: true,
    options: ["Soft blue", "Graphite", "Pastel green"],
    helper: "Choose the minimalist color palette for the UI.",
  },
  {
    name: "sidebarColor",
    label: "Sidebar background color",
    type: "color",
    placeholder: "#FFFFFF",
    required: true,
    helper: "Background color for the left navigation sidebar.",
  },
  {
    name: "sidebarTextColor",
    label: "Sidebar text color",
    type: "color",
    placeholder: "#374151",
    required: true,
    helper: "Text and icon color for sidebar navigation items.",
  },
  {
    name: "topbarColor",
    label: "Top bar background color",
    type: "color",
    placeholder: "#FFFFFF",
    required: true,
    helper: "Background color for the top navigation bar.",
  },
  {
    name: "topbarTextColor",
    label: "Top bar text color",
    type: "color",
    placeholder: "#111827",
    required: true,
    helper: "Text color for the top navigation bar.",
  },
  {
    name: "heroHeadline",
    label: "Hero headline",
    type: "textarea",
    placeholder: "A modern community platform that stays simple.",
    required: true,
    helper: "Use a short, crisp headline for the landing experience.",
  },
  {
    name: "navigationStyle",
    label: "Navigation style",
    type: "select",
    placeholder: "Choose navigation",
    required: true,
    options: ["Floating top bar", "Sidebar compact", "Bottom menu"],
    helper: "Select the navigation pattern for this theme.",
  },
  {
    name: "buttonShape",
    label: "Button shape",
    type: "select",
    placeholder: "Choose shape",
    required: true,
    options: ["Rounded", "Pill", "Sharp"],
    helper: "Control how CTA buttons appear across the theme.",
  },
  {
    name: "enableTestimonials",
    label: "Show testimonials",
    type: "select",
    placeholder: "Choose option",
    required: true,
    options: ["Yes", "No"],
    helper: "Turn dedicated testimonial blocks on or off.",
  },
];

const ACCENT_PALETTE_HEX: Record<string, string> = {
  "Soft blue": "#6366F1",
  "Graphite": "#4B5563",
  "Pastel green": "#10B981",
};

const featureList = [
  "Minimal visual hierarchy with crisp spacing",
  "Adaptive navigation for mobile-first use",
  "Strict required fields for safe theme deployment",
  "Optimized backend payload structure",
];

const palette: ThemePayload = {
  sidebarBackground: "#FFFFFF",
  sidebarTextColor: "#374151",
  topbarBackground: "#FFFFFF",
  topbarTextColor: "#111827",
  primaryAccent: "#6366F1",
  surface: "#F9FAFB",
};

const ThemeModernMinimal: React.FC = () => (
  <ThemePageShell
    title="Modern Minimal Theme"
    subtitle="A clean, refined experience built for societies and campuses that want fast onboarding, calm interface flow, and secure theme configuration."
    description="Fill the minimal theme fields to ensure every page element is validated and ready for secure API submission. This premium theme keeps the frontend thin and the backend validations strong."
    featureList={featureList}
    fields={fields}
    cta="Save Minimal Theme"
    pathLabel="Premium Themes"
    themePayload={palette}
    baseSlug="modern-minimal"
    liveFieldNames={["accentPalette", "sidebarColor", "sidebarTextColor", "topbarColor", "topbarTextColor"]}
    getLiveOverrides={(values) => {
      const overrides: Partial<ThemePayload> = {};
      const accent = ACCENT_PALETTE_HEX[values.accentPalette];
      if (accent) overrides.primaryAccent = accent;
      if (isValidHex(values.sidebarColor)) overrides.sidebarBackground = values.sidebarColor;
      if (isValidHex(values.sidebarTextColor)) overrides.sidebarTextColor = values.sidebarTextColor;
      if (isValidHex(values.topbarColor)) overrides.topbarBackground = values.topbarColor;
      if (isValidHex(values.topbarTextColor)) overrides.topbarTextColor = values.topbarTextColor;
      return overrides;
    }}
  />
);

export default ThemeModernMinimal;
